import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemSeparator,
  ItemTitle,
  Spinner,
} from '@1trading/react';
import { ArrowRight } from 'lucide-react';
import { Fragment } from 'react';
import { closeOnly } from '@/components/dialogs/close-only';
import type { UnconfiguredMarketMaker } from '@/services/api/mm-programs';

interface UnconfiguredAccountListProps {
  accounts: UnconfiguredMarketMaker[];
  /** The pid being set up, while its request runs */
  pendingPid: string | null;
  /** Left out for viewers who can't edit, which hides the Set up buttons */
  onSetUp?: (pid: string) => void;
}

export const UnconfiguredAccountList = ({ accounts, pendingPid, onSetUp }: UnconfiguredAccountListProps) => (
  <ItemGroup>
    {accounts.map(({ fastId, name, pid }, index) => (
      <Fragment key={fastId}>
        {index > 0 ? <ItemSeparator /> : null}
        <Item className="px-0 py-2" size="sm">
          <ItemContent className="gap-0">
            <ItemTitle>{name || 'Unnamed account'}</ItemTitle>
            <ItemDescription className="text-xs tabular-nums">FAST {fastId}</ItemDescription>
          </ItemContent>
          {onSetUp ? (
            <ItemActions>
              <Button
                aria-label={`Set up ${name || `FAST ${fastId}`}`}
                className="gap-1"
                disabled={!pid || pendingPid !== null}
                onClick={() => pid && onSetUp(pid)}
                size="sm"
                title={pid ? undefined : 'This MM row has no pid yet; run the MM backfill'}
                type="button"
                variant="outline"
              >
                Set up
                {pid && pid === pendingPid ? <Spinner className="size-3.5" /> : <ArrowRight className="size-3.5" />}
              </Button>
            </ItemActions>
          ) : null}
        </Item>
      </Fragment>
    ))}
  </ItemGroup>
);
UnconfiguredAccountList.displayName = 'UnconfiguredAccountList';

interface PickMarketMakerDialogProps {
  candidates: UnconfiguredMarketMaker[];
  pendingPid: string | null;
  onClose: () => void;
  onPick: (pid: string) => void;
}

export const PickMarketMakerDialog = ({ candidates, pendingPid, onClose, onPick }: PickMarketMakerDialogProps) => (
  <Dialog onOpenChange={pendingPid ? undefined : closeOnly(onClose)} open>
    <DialogContent aria-describedby={undefined} className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Set up a market maker</DialogTitle>
      </DialogHeader>
      {candidates.length ? (
        <UnconfiguredAccountList accounts={candidates} onSetUp={onPick} pendingPid={pendingPid} />
      ) : (
        <p className="text-sm text-muted-foreground">Every market maker has settings. An account appears here once its segment is changed to MARKET_MAKER.</p>
      )}
    </DialogContent>
  </Dialog>
);
PickMarketMakerDialog.displayName = 'PickMarketMakerDialog';
